import type { Station } from '@hcc/types';

/**
 * Per-item field config for the kit-issue and kit-return form rows.
 * Different kinds of kit have meaningfully different identifiers:
 * a laptop has a serial number, a phone has an IMEI, a van has a
 * registration, a pair of boots has a size. Showing every field on
 * every row clutters the UI and invites the wrong identifier being
 * captured ("Size: SN-1234").
 *
 * Two inputs drive the config:
 *   - the asset's category (if we know it — tracked assets carry one)
 *   - the station as a sensible fallback (IT defaults to serial, PPE
 *     defaults to size, etc).
 *
 * Categories are matched case-insensitively against a small canonical
 * map; unknown categories fall through to the station default.
 */

export interface ItemFieldsConfig {
  /** Identifier field — serial / IMEI / ICCID / registration. */
  identifier: { show: boolean; label: string; placeholder?: string };
  /** Size — relevant for PPE consumables. */
  size: { show: boolean; label: string; placeholder?: string };
}

interface CategoryConfig {
  identifier?: { label: string; placeholder?: string } | false;
  size?: { label?: string; placeholder?: string } | false;
}

// Categories keyed by their canonical lowercased form. Aliases below
// map common variants to the canonical key.
const CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  laptop: { identifier: { label: 'Serial', placeholder: 'SN-1234-5678' }, size: false },
  desktop: { identifier: { label: 'Serial' }, size: false },
  monitor: { identifier: { label: 'Serial' }, size: false },
  tablet: { identifier: { label: 'Serial' }, size: false },
  phone: { identifier: { label: 'IMEI', placeholder: '15-digit IMEI' }, size: false },
  sim: { identifier: { label: 'ICCID', placeholder: '19-20 digits' }, size: false },
  headset: { identifier: { label: 'Serial' }, size: false },
  dock: { identifier: { label: 'Serial' }, size: false },
  webcam: { identifier: { label: 'Serial' }, size: false },
  charger: { identifier: false, size: false },
  cable: { identifier: false, size: false },
  // Vehicles
  vehicle: { identifier: { label: 'Registration', placeholder: 'AB23 XYZ' }, size: false },
  van: { identifier: { label: 'Registration', placeholder: 'AB23 XYZ' }, size: false },
  car: { identifier: { label: 'Registration' }, size: false },
  truck: { identifier: { label: 'Registration' }, size: false },
  trailer: { identifier: { label: 'Registration' }, size: false },
  // PPE consumables — size matters, serial doesn't
  boots: { identifier: false, size: { label: 'Size', placeholder: 'UK 10' } },
  footwear: { identifier: false, size: { label: 'Size' } },
  'hi-vis': { identifier: false, size: { label: 'Size', placeholder: 'S / M / L / XL' } },
  hivis: { identifier: false, size: { label: 'Size' } },
  'high-vis': { identifier: false, size: { label: 'Size' } },
  helmet: { identifier: false, size: { label: 'Size' } },
  hardhat: { identifier: false, size: { label: 'Size' } },
  'hard hat': { identifier: false, size: { label: 'Size' } },
  gloves: { identifier: false, size: { label: 'Size' } },
  trousers: { identifier: false, size: { label: 'Size' } },
  jacket: { identifier: false, size: { label: 'Size' } },
  'eye protection': { identifier: false, size: { label: 'Size' } },
  'ear protection': { identifier: false, size: { label: 'Size' } },
  mask: { identifier: false, size: { label: 'Size' } },
  // Tools
  drill: { identifier: { label: 'Serial' }, size: false },
  ladder: { identifier: { label: 'Asset / Serial' }, size: false },
  toolbox: { identifier: { label: 'Asset / Serial' }, size: false },
  'hand tools': { identifier: { label: 'Asset / Serial' }, size: false },
  'power tool': { identifier: { label: 'Serial' }, size: false },
  // DBS / HR placeholder
  document: { identifier: false, size: false },
};

const STATION_DEFAULTS: Record<Station, ItemFieldsConfig> = {
  IT: {
    identifier: { show: true, label: 'Serial', placeholder: 'SN-... or IMEI' },
    size: { show: false, label: 'Size' },
  },
  PPE: {
    identifier: { show: false, label: 'Serial' },
    size: { show: true, label: 'Size', placeholder: 'S / M / L / 10' },
  },
  Tools: {
    identifier: { show: true, label: 'Serial' },
    size: { show: false, label: 'Size' },
  },
  Fleet: {
    identifier: { show: true, label: 'Registration', placeholder: 'AB23 XYZ' },
    size: { show: false, label: 'Size' },
  },
  DBSHR: {
    identifier: { show: false, label: '' },
    size: { show: false, label: '' },
  },
};

export function fieldsFor(
  station: Station,
  category?: string | null,
): ItemFieldsConfig {
  const stationDefault = STATION_DEFAULTS[station];
  if (!category) return stationDefault;

  const key = category.trim().toLowerCase();
  const cat = CATEGORY_CONFIG[key];
  if (!cat) return stationDefault;

  return {
    identifier:
      cat.identifier === false
        ? { show: false, label: stationDefault.identifier.label }
        : {
            show: true,
            label: cat.identifier?.label ?? stationDefault.identifier.label,
            ...(cat.identifier?.placeholder
              ? { placeholder: cat.identifier.placeholder }
              : {}),
          },
    size:
      cat.size === false
        ? { show: false, label: stationDefault.size.label }
        : {
            show: true,
            label: cat.size?.label ?? stationDefault.size.label,
            ...(cat.size?.placeholder ? { placeholder: cat.size.placeholder } : {}),
          },
  };
}
